import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';

/**
 * Writes or revises the closure report.
 *
 * ⚠️ No outcome field. The outcome belongs to the mission and was fixed when it was closed, and the
 * endpoint does not accept one — a mission that ended two ways would make §23.10's statistics depend
 * on which table you counted.
 */
export function MissionReportDialog({
  open,
  onOpenChange,
  initialSummary,
  onConfirm,
  busy,
  serverError,
  title,
  lede,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** The existing text when revising, so an edit does not start from a blank box. */
  initialSummary: string;
  onConfirm: (summary: string) => void;
  busy: boolean;

  /** Shown inside the dialog — see CloseMissionDialog for why behind it is wrong. */
  serverError?: string | null;

  /**
   * Wording, when this is not the mission's own closure report.
   *
   * ⚠️ Overridable rather than a second near-identical dialog: §23.3's round report is the same box
   * with the same rules — trim, refuse empty, re-seed on open so a stale copy cannot overwrite
   * somebody else's edit — and a copy of this file would have drifted on the third of those.
   */
  title?: string;
  lede?: string;
}) {
  const { t } = useLocale();
  const [summary, setSummary] = useState(initialSummary);
  const [error, setError] = useState<string | null>(null);

  // Re-seeded whenever the dialog opens: the report may have been rewritten by another operator
  // since this component mounted, and editing a stale copy would overwrite their text with it.
  useEffect(() => {
    if (open) {
      setSummary(initialSummary);
      setError(null);
    }
  }, [open, initialSummary]);

  const submit = () => {
    const text = summary.trim();

    if (!text) {
      setError(t.missions.detailReportSummaryRequired);
      return;
    }

    onConfirm(text);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t.missions.closeAria}>
        <DialogHeader>
          <DialogTitle>{title ?? t.missions.detailReportDialogTitle}</DialogTitle>
          <DialogDescription>{lede ?? t.missions.detailReportDialogLede}</DialogDescription>
        </DialogHeader>

        <Field label={t.missions.detailReportSummaryLabel} htmlFor="report-summary">
          <textarea
            id="report-summary"
            rows={8}
            value={summary}
            onChange={(event) => {
              setSummary(event.target.value);
              setError(null);
            }}
            className="w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-start outline-none focus:border-primary"
          />
        </Field>

        {error ?? serverError ? (
          <p role="alert" className="text-sm text-danger">
            {error ?? serverError}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {t.missions.cancel}
          </Button>
          <Button onClick={submit} disabled={busy}>
            {t.missions.detailReportSave}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
